/**
 * Order Log — desktop column selection, remembered (ORD-1.3)
 * ─────────────────────────────────────────────────────────────────────────────
 * "Given I have set a column selection, when I navigate away and return in a
 * later session, then my selection is restored. Column selection persists per
 * user, per log type."
 *
 * Stored in local storage under a key scoped by user id and log type, the same
 * shape Snapshot pins use (`snapshotPinsApi.storageKey`). Scoping the key — not
 * deleting it at logout — is what keeps one account from inheriting another's
 * view: a second user on the same browser simply reads a different key, and the
 * first user's selection is still there when they log back in.
 *
 * Device-local by nature. If the selection ever has to follow a user across
 * devices this needs a server-side preference instead.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { OPERATIONS_PRESET, defaultVisibleColumns, type OrderLogColumnDef } from './orderLogModel';

const STORAGE_PREFIX = 'reports.orderLog.columns';
const STORE_VERSION = 1;

/** Log types are storage keys — renaming one silently resets that log's saved selection. */
export type OrderLogColumnScope =
    | 'snapshot-paid'
    | 'snapshot-unpaid'
    | 'snapshot-unpaid-cancelled'
    | 'sales-paid';

/**
 * `known` is every column key that existed when the selection was saved. It is
 * what separates "the user switched this off" from "this column did not exist
 * yet", so a column added to the product later can come back at its own default
 * state instead of looking deliberately unchecked.
 */
interface StoredSelection {
    v: number;
    enabled: string[];
    known: string[];
}

const storageKey = (scope: string, userId: string) => `${STORAGE_PREFIX}::${userId || 'anon'}::${scope}`;

/** A column's product-defined default state — the Operations preset, plus the always-visible ones. */
const defaultOn = (c: OrderLogColumnDef) => !!c.pinned || !!c.locked || OPERATIONS_PRESET.indexOf(c.key) >= 0;

const isStringArray = (v: any): v is string[] => Array.isArray(v) && v.every(x => typeof x === 'string');

function read(key: string): StoredSelection | null {
    try {
        const raw = window.localStorage.getItem(key);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        // Anything written by an older/newer format is ignored rather than trusted —
        // a bad read falls back to the preset instead of throwing at the user.
        if (!parsed || parsed.v !== STORE_VERSION || !isStringArray(parsed.enabled) || !isStringArray(parsed.known)) return null;
        return parsed as StoredSelection;
    } catch {
        return null;
    }
}

function write(key: string, selection: StoredSelection | null): void {
    try {
        if (selection) window.localStorage.setItem(key, JSON.stringify(selection));
        else window.localStorage.removeItem(key);
    } catch {
        /* private mode / storage disabled — the selection holds for this session only */
    }
}

/**
 * Turns a saved selection into the keys to render against the CURRENT registry.
 * Both of ORD-1.3's saved-selection edge cases resolve here, on read rather than
 * on write, because outlet configuration can change between sessions:
 *  · a column since suppressed by outlet config drops out, the rest is preserved;
 *  · a column added since the save falls back to its own default state.
 */
export function resolveVisibleColumns(stored: StoredSelection | null, columns: OrderLogColumnDef[]): string[] {
    if (!stored) return defaultVisibleColumns(columns);
    const live = new Set(columns.map(c => c.key));
    const known = new Set(stored.known);
    // An explicitly emptied selection stays empty — leaving only the locked columns
    // is a valid minimal state, not a signal to restore the preset.
    const keys = stored.enabled.filter(k => live.has(k));
    for (const c of columns) {
        if (!known.has(c.key) && defaultOn(c)) keys.push(c.key);
    }
    return Array.from(new Set(keys));
}

export interface OrderLogColumnPrefs {
    /** Raw preference; `null` means "follow the Operations preset", exactly as the old local state did. */
    stored: string[] | null;
    setStored: (next: string[] | null | ((prev: string[] | null) => string[] | null)) => void;
    /** The keys to render, filtered against the registry passed in. */
    resolve: (columns: OrderLogColumnDef[]) => string[];
}

export function useOrderLogColumns(scope: OrderLogColumnScope, userId: string): OrderLogColumnPrefs {
    const key = storageKey(scope, userId);
    const [selection, setSelection] = useState<StoredSelection | null>(() => read(key));
    // Mirrors `selection` so the setter can resolve a functional update without
    // writing to storage from inside a state updater (StrictMode runs those twice).
    const selectionRef = useRef(selection);
    // The registry `resolve` last saw, so a write can record what existed at the time.
    const registryRef = useRef<string[] | null>(null);

    // `userId` arrives after auth hydrates, and changes when the account does — both
    // move the key, so re-read rather than carrying the previous user's selection over.
    useEffect(() => {
        const loaded = read(key);
        selectionRef.current = loaded;
        setSelection(loaded);
    }, [key]);

    const setStored: OrderLogColumnPrefs['setStored'] = useCallback(next => {
        const prev = selectionRef.current?.enabled ?? null;
        const enabled = typeof next === 'function' ? next(prev) : next;
        const resolved: StoredSelection | null = enabled === null
            ? null
            : { v: STORE_VERSION, enabled, known: registryRef.current ?? enabled };
        selectionRef.current = resolved;
        setSelection(resolved);
        write(key, resolved);
    }, [key]);

    const resolve = useCallback((columns: OrderLogColumnDef[]) => {
        registryRef.current = columns.map(c => c.key);
        return resolveVisibleColumns(selection, columns);
    }, [selection]);

    return { stored: selection?.enabled ?? null, setStored, resolve };
}
